import {
  Button,
  Card,
  Col,
  Create,
  Divider,
  Form,
  Input,
  Row,
  Select,
  Space,
  Tag,
  notification,
  useForm,
} from '@pankod/refine-antd';
import { useNavigation } from '@pankod/refine-core';
import { IFormValues, MakePayment } from 'components';
import { SingleDatePicker } from 'components/date-picker/SingleDatePicker';
import { ModuleExpenseDrawer } from 'components/drawer/ModuleExpenseDrawer';
import { ProductSaleTable } from 'components/product-sale-table';
import dayjs from 'dayjs';
import { EPaymentType, ESalesChannel, EStatus } from 'enums';
import { IClient, ISale } from 'interfaces';
import { useEffect, useReducer, useRef, useState } from 'react';
import { ActionKind, initialState, productSaleReducer } from 'reducers/product-sale-reducer';
import { getClient, getClientsDropdown } from 'services/client';
import { ICreateExpenseReturnType } from 'services/expense';
import { deleteExpenseCategory } from 'services/expense-category';
import { createPaymentLog } from 'services/payment';
import { getPOCOfClient } from 'services/point-of-contact';
import { CreateSaleData, createSale } from 'services/sale';
import { getTransactionToken } from 'services/transaction';
import { getWarehousesDropdown, IWarehouseDropDown } from 'services/warehouse';
import { calculateSaleNetTotal } from 'utilities/sale-totals';
import { useOrganizationVatRate } from 'hooks/useOrganizationVatRate';
import { useFeatureFlag } from 'hooks/useFeatureFlags';
import { InvoiceProfileSelect } from 'components/invoice-profile/InvoiceProfileSelect';
import { getAddressAsString } from 'utilities/shared';

interface IClientPOC {
  id: string;
  name: string;
}

const { Option } = Select;

export const SaleCreate: React.FC = () => {
  const { list } = useNavigation();
  const { form, formProps, saveButtonProps, queryResult } = useForm<ISale>({
    metaData: {
      fields: ['id'],
    },
  });

  const [warehouses, setWarehouses] = useState<IWarehouseDropDown[]>([]);
  const [clients, setClients] = useState<
    Omit<IClient, 'address' | 'pointOfContacts' | 'clientPhoto' | 'clientTags' | 'payMethods'>[]
  >([]);
  const [clientPOCs, setClientPOCs] = useState<IClientPOC[]>([]);
  const [isDrawerVisible, setIsDrawerVisible] = useState<boolean>(false);
  const [isCreating, setIsCreating] = useState<boolean>(false);
  const [isPaymentLoading, setIsPaymentLoading] = useState<boolean>(false);
  const [isFetching, setIsFetching] = useState<boolean>(false);
  const [salesDate, setSalesDate] = useState<Date>();
  const [isExpenseVisible, setIsExpenseVisible] = useState(false);
  const [expenses, setExpenses] = useState<ICreateExpenseReturnType[]>([]);
  const [
    {
      saleProducts,
      warehouseId,
      vat,
      shipment,
      discountCost,
      invoiceDiscountEntry,
      paidInCash,
      payNow,
      fullDue,
    },
    dispatch,
  ] = useReducer(productSaleReducer, initialState);
  // With `sale.vatRate` on, the sale shows the organization rate; the API copies the same rate.
  const { vatRatePercent } = useOrganizationVatRate();
  const invoiceProfilesEnabled = useFeatureFlag('sale.invoiceProfiles');
  useEffect(() => {
    dispatch({ type: ActionKind.SetVat, payload: vatRatePercent });
  }, [vatRatePercent]);
  const [warehouseDropdownOpen, setWarehouseDropdownOpen] = useState<boolean>(false);

  const clientIdRef = useRef<string>();
  const transactionTokenRef = useRef<string>();
  const transactionTokenRequestRef = useRef<boolean>(false);
  const warehouseSelectRef = useRef<any>(null);
  const clientSelectRef = useRef<any>(null);
  const datePickerRef = useRef<any>(null);
  const pocSelectRef = useRef<any>(null);
  const salesAddressRef = useRef<any>(null);

  useEffect(() => {
    (async () => {
      setIsFetching(true);
      try {
        const [warehousesResponse, clientsResponse] = await Promise.all([
          getWarehousesDropdown(),
          getClientsDropdown(),
        ]);
        const warehousesData = warehousesResponse.map((warehouse) => {
          return {
            id: warehouse.id,
            name: warehouse.name,
          };
        });

        setClients(clientsResponse);
        setWarehouses(warehousesData);

        // after fetching the data, focus on the warehouse dropdown
        setTimeout(() => {
          warehouseSelectRef.current?.focus();
          setWarehouseDropdownOpen(true);
        }, 0);
      } catch (err: any) {
        notification.error({
          message: 'Failed',
          description: err.message,
        });
        setClients([]);
        setWarehouses([]);
      }
      setIsFetching(false);
    })();
  }, []);

  const addExpenses = (createdExpense: ICreateExpenseReturnType) => {
    setExpenses((prevExpenses) => {
      return [...prevExpenses, createdExpense];
    });
  };

  const getOptions = (items: IClient[] | IClientPOC[] | IWarehouseDropDown[]) => {
    return items.map((item) => (
      <Option key={item.id} value={item.id}>
        {item.name}
      </Option>
    ));
  };

  const onSelectWarehouse = async (warehouseId: string | undefined) => {
    if (!warehouseId) return;
    dispatch({ type: ActionKind.SetWarehouseId, payload: warehouseId });

    // after selecting the warehouse, focus on the client dropdown
    setTimeout(() => {
      clientSelectRef.current?.focus();
    }, 0);
  };

  const onSelectClient = async (clientId: string) => {
    // after selecting the client, focus on the date picker
    setTimeout(() => {
      datePickerRef.current?.focus();
    }, 0);

    try {
      const [client, pocs] = await Promise.all([getClient(clientId), getPOCOfClient(clientId)]);
      const address = client.address;
      const addressString = getAddressAsString(address || {});

      setClientPOCs([...pocs]);
      form.setFieldsValue({
        salesAddress: addressString,
        clientPoc: pocs[0]?.id,
      });
    } catch (err: any) {
      setClientPOCs([]);
      form.setFieldsValue({
        salesAddress: undefined,
        clientPoc: undefined,
      });
      notification.error({
        message: 'Failed',
        description: err.message,
      });
    }
  };

  const totalAmount = saleProducts.reduce((total, product) => {
    total += product.quantity * product.unitPrice;
    return total;
  }, 0);
  const totalAmountDiscount = saleProducts.reduce((discount, product) => {
    discount += product.discountCost || 0;
    return discount;
  }, 0);
  const netTotal = calculateSaleNetTotal({
    saleProducts,
    vatPercentage: vat,
    shipmentCost: shipment,
    invoiceDiscountCost: discountCost,
  });

  const getSalesProductsPayload = (): CreateSaleData['salesProducts'] =>
    saleProducts.map((product) => ({
      sku: product.sku,
      quantity: product.quantity,
      unitPrice: product.unitPrice,
      productVariantId: product.productVariantId,
      discountCost: product.discountCost || 0,
      discountEntryValue: product.discountEntryValue ?? null,
      discountEntryType: product.discountEntryType ?? null,
    }));

  const onFinish = async (values: any) => {
    if (payNow) {
      setIsDrawerVisible(true);
      return;
    }

    if (transactionTokenRequestRef.current === true) return;

    try {
      setIsCreating(true);
      const salesPaymentsPayload = paidInCash
        ? [
            {
              paymentType: EPaymentType.Cash,
              currentPaidAmount: netTotal,
              paymentDate: values.salesDate ? new Date(values.salesDate) : new Date(),
            },
          ]
        : [];

      if (!transactionTokenRef.current && transactionTokenRequestRef.current === false) {
        transactionTokenRequestRef.current = true;
        transactionTokenRef.current = await getTransactionToken();
      }
      if (discountCost + totalAmountDiscount > totalAmount) {
        throw new Error('Total discount cannot be higher than total amount');
      }
      const saleData: CreateSaleData = {
        client: values.client as string,
        newClientDetails: null,
        clientPoc: values.clientPoc as string,
        salesDate: values.salesDate ? new Date(values.salesDate) : new Date(),
        salesStatus: EStatus.CONFIRMED,
        warehouse: values.warehouse as string,
        salesAddress: values.salesAddress as string,
        note: values.note as string,
        invoiceProfileId: values.invoiceProfileId as string | undefined,
        vatAmount: vat,
        shipmentCost: shipment,
        discountCost: discountCost,
        ...invoiceDiscountEntry,
        salesPayments: salesPaymentsPayload,
        salesChannel: ESalesChannel.SALES,
        salesProducts: getSalesProductsPayload(),
        salesExpense: expenses.map((expense) => ({
          id: expense.id,
        })),
        transactionToken: transactionTokenRef.current!,
      };

      await createSale(saleData);
      notification.success({
        message: 'Success',
        description: 'Successfully created a sale.',
      });
      list('sales');
    } catch (err: any) {
      transactionTokenRef.current = undefined;
      transactionTokenRequestRef.current = false;
      notification.error({
        message: 'Failed',
        description: err.message,
      });
    } finally {
      setIsCreating(false);
    }
  };

  const onPaymentSubmit = async (data: IFormValues) => {
    if (transactionTokenRequestRef.current === true) return;

    setIsPaymentLoading(true);
    try {
      const formValues = form.getFieldsValue([
        'warehouse',
        'client',
        'clientPoc',
        'salesAddress',
        'salesDate',
        'note',
        'invoiceProfileId',
      ]);

      if (!transactionTokenRef.current && transactionTokenRequestRef.current === false) {
        transactionTokenRequestRef.current = true;
        transactionTokenRef.current = await getTransactionToken();
      }

      const saleData: CreateSaleData = {
        client: formValues.client as string,
        newClientDetails: null,
        clientPoc: formValues.clientPoc as string,
        warehouse: formValues.warehouse as string,
        salesAddress: formValues.salesAddress as string,
        note: formValues.note as string,
        invoiceProfileId: formValues.invoiceProfileId as string | undefined,
        salesDate: formValues.salesDate ? new Date(formValues.salesDate) : new Date(),
        salesStatus: EStatus.CONFIRMED,
        vatAmount: vat,
        shipmentCost: shipment,
        discountCost: discountCost,
        ...invoiceDiscountEntry,
        salesChannel: ESalesChannel.SALES,
        salesProducts: getSalesProductsPayload(),
        salesExpense: expenses.map((expense) => ({
          id: expense.id,
        })),
        transactionToken: transactionTokenRef.current!,
      };

      const response = await createSale(saleData);
      const paymentData = {
        paymentType: data.paymentType,
        paymentMethod: data.paymentMethod,
        paymentDetails: data.paymentDetails,
        currentPaidAmount: data.currentPaidAmount,
        paymentDate: formValues.salesDate ? new Date(formValues.salesDate) : new Date(),
        attachments: data.attachments,
        companyBank: data.companyBank,
        sales: response.id,
      };

      await createPaymentLog('sale', paymentData);
      notification.success({
        message: 'Success',
        description: 'Successfully created a sale.',
      });
      list('sales');
    } catch (err: any) {
      transactionTokenRef.current = undefined;
      transactionTokenRequestRef.current = false;
      notification.error({
        message: 'Failed',
        description: err.message,
      });
    }
    setIsPaymentLoading(false);
  };

  const renderedExpenses = expenses.map((expense) => {
    const paymentMethodBankName = expense.paymentMethod ? `-${expense.paymentMethod.bankName}` : '';
    const tagString = `${expense.category.name}${paymentMethodBankName}-${expense.amount}`;
    return (
      <Tag
        key={expense.id}
        closable
        onClose={() => {
          try {
            deleteExpenseCategory(expense.id);
            setExpenses((prevExpenses) => prevExpenses.filter((item) => item.id !== expense.id));
          } catch (err: any) {
            notification.error({
              message: 'Failed',
              description: err.message,
            });
          }
        }}
      >
        {tagString}
      </Tag>
    );
  });

  return (
    <>
      <ModuleExpenseDrawer
        onClose={() => setIsExpenseVisible(false)}
        visible={isExpenseVisible}
        addExpenses={addExpenses}
        title="Add Expense"
      />

      <Create
        isLoading={queryResult?.isFetching}
        saveButtonProps={{
          ...saveButtonProps,
          disabled: !saleProducts.length,
          loading: isCreating,
        }}
      >
        <Form
          {...formProps}
          layout="vertical"
          initialValues={{
            isActive: true,
          }}
          onFinish={onFinish}
        >
          <Row justify="space-around" align="middle">
            <Col xs={24} lg={18}>
              <Card title="Sale Information">
                <Form.Item
                  label="Warehouse"
                  name="warehouse"
                  rules={[{ required: true, message: 'Please select a warehouse' }]}
                >
                  <Select
                    ref={warehouseSelectRef}
                    autoFocus
                    open={warehouseDropdownOpen}
                    showSearch
                    placeholder="Select a warehouse"
                    loading={isFetching}
                    disabled={isFetching || !warehouses.length}
                    onSelect={(value: string) => {
                      setWarehouseDropdownOpen(false);
                      onSelectWarehouse(value?.toString());
                    }}
                    onDropdownVisibleChange={(visible) => setWarehouseDropdownOpen(visible)}
                    filterOption={(input, option: any) =>
                      option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                    }
                  >
                    {getOptions(warehouses)}
                  </Select>
                </Form.Item>
                <Form.Item
                  label="Client's Name"
                  name="client"
                  rules={[{ required: true, message: 'Please select a client' }]}
                >
                  <Select
                    ref={clientSelectRef}
                    showSearch
                    placeholder="Select a client"
                    loading={isFetching}
                    disabled={isFetching || !clients.length}
                    onSelect={onSelectClient}
                    filterOption={(input, option: any) =>
                      option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                    }
                  >
                    {getOptions(clients)}
                  </Select>
                </Form.Item>
                <Form.Item label="Date" name="salesDate">
                  <SingleDatePicker
                    ref={datePickerRef}
                    value={dayjs(salesDate)}
                    onChange={(value: any, _formatString: string) => {
                      setSalesDate(new Date(value.toDate()));
                      pocSelectRef.current?.focus();
                    }}
                  />
                </Form.Item>
                {invoiceProfilesEnabled && (
                  <Form.Item label="Invoice header" name="invoiceProfileId">
                    <InvoiceProfileSelect />
                  </Form.Item>
                )}
                <Form.Item label="Point Of Contact" name="clientPoc">
                  <Select
                    ref={pocSelectRef}
                    showSearch
                    placeholder="Select a POC"
                    filterOption={(input, option: any) =>
                      option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                    }
                    onDropdownVisibleChange={(visible) => {
                      if (!visible) {
                        salesAddressRef.current?.focus();
                      }
                    }}
                  >
                    {getOptions(clientPOCs)}
                  </Select>
                </Form.Item>
                <Form.Item label="Sale Address" name="salesAddress">
                  <Input.TextArea ref={salesAddressRef} rows={4} />
                </Form.Item>
                <Form.Item label="Note" name="note">
                  <Input.TextArea rows={4} />
                </Form.Item>
              </Card>
            </Col>
          </Row>

          <Divider />

          <Row justify="space-around" align="middle">
            <Col xs={24} lg={18}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <Button onClick={() => setIsExpenseVisible(true)} size="large">
                  Add Expense
                </Button>
              </div>
            </Col>
          </Row>

          <Row justify="center" style={{ marginTop: '1rem' }}>
            <Col span={12}>
              <Space size={[8, 16]} wrap>
                {renderedExpenses}
              </Space>
            </Col>
          </Row>

          <Divider />

          <Row justify="space-around" align="middle">
            <Col xs={24} lg={18}>
              <ProductSaleTable
                saleProducts={saleProducts}
                warehouse={warehouseId}
                vat={vat}
                shipment={shipment}
                discountCost={discountCost}
                paidInCash={paidInCash}
                payNow={payNow}
                fullDue={fullDue}
                productDispatcher={dispatch}
              />
            </Col>
          </Row>
        </Form>
        {clientIdRef.current && (
          <MakePayment
            paymentBy="client"
            paymentById={clientIdRef.current}
            totalPayableAmount={netTotal}
            defaultAmount={netTotal}
            visible={isDrawerVisible}
            loading={isPaymentLoading}
            closeDrawer={() => setIsDrawerVisible(false)}
            onPaymentSubmit={onPaymentSubmit}
          />
        )}
      </Create>
    </>
  );
};
